// @owners { team: patients-team }
import { ActionSheetContext, AltoIcon, Button, Card, Description, ListItem, Row } from '@alto/design-system';
import { useQueryClient } from '@tanstack/react-query';
import pluralize from 'pluralize';
import React, { useContext, useEffect } from 'react';
import { push } from 'react-router-redux';
import { logout } from '~shared/actions/auth';
// eslint-disable-next-line import/no-deprecated
import { fetchUsers } from '~shared/actions/users';
import { getFamilyMembers } from '~shared/features/family-accounts/selectors/getFamilyMembers';
import { getCurrentUser } from '~shared/features/users/selectors/getCurrentUser';
import { getDisplayFullName } from '~shared/helpers/helper';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { ReferralActionSheet } from './ReferralActionSheet';
import { ContentWrapper, Main, PageBackground, Sidebar } from '~web/components/Page';
import { PageHeader } from '~web/components/PageHeader';
import { configureStore } from '~web/configureStore';
import { SidebarItem } from '~web/features/my-meds/components/SidebarItem';
import referral from '~web/images/inviteFriends@2x.png';

export const Profile = () => {
  const { setActiveActionSheet } = useContext(ActionSheetContext);
  const queryClient = useQueryClient();
  const { trackEvent, trackPageView } = useAnalytics();
  const dispatch = useDispatchShared();
  const users = useSelectorShared(getFamilyMembers);
  const user = useSelectorShared(getCurrentUser);
  const isFamilyMember = user?.is_family_member ?? false;

  const handleLogout = async () => {
    trackEvent({ event: EVENTS.LOGOUT_CLICKED });
    const success = await dispatch(logout());
    if (success) {
      dispatch(push('/'));
    }
    configureStore().persistor.purge();
    queryClient.clear();
  };

  useEffect(() => {
    trackPageView({ event: EVENTS.PROFILE_VIEWED });
  }, [trackPageView]);

  useEffect(() => {
    // eslint-disable-next-line import/no-deprecated
    dispatch(fetchUsers);
  }, [dispatch]);

  return (
    <PageBackground>
      <ContentWrapper>
        <Row
          flexGrow={1}
          spaceBetween
        >
          <PageHeader>Account</PageHeader>
          <Button
            accessibilityLabel="Log out"
            label="Log out"
            onPress={handleLogout}
            small
            type="secondary"
          />
        </Row>
        <Main>
          <Card title="Settings">
            <ListItem
              LeftContent={<AltoIcon name="mail" />}
              RightContent={<AltoIcon name="chevronright" />}
              title="Contact & Login Info"
              descriptions={<Description>Email, Password & Phone Number</Description>}
              onPress={() => {
                dispatch(push('/me/contact-info'));
              }}
            />
            <ListItem
              LeftContent={<AltoIcon name="sync" />}
              RightContent={<AltoIcon name="chevronright" />}
              title="Auto Refill"
              onPress={() => {
                dispatch(push('/me/auto-refills'));
              }}
            />
            <ListItem
              LeftContent={<AltoIcon name="creditcard" />}
              RightContent={<AltoIcon name="chevronright" />}
              title="Payments and Receipts"
              onPress={() => {
                dispatch(push('/me/payments'));
              }}
            />
            <ListItem
              LeftContent={<AltoIcon name="mapmarker" />}
              RightContent={<AltoIcon name="chevronright" />}
              title="Delivery Addresses"
              onPress={() => {
                dispatch(push('/me/addresses'));
              }}
            />
          </Card>
          <Card title={pluralize('Profile', users.length)}>
            {users.map((user) => {
              return (
                <ListItem
                  key={user.id}
                  LeftContent={<AltoIcon name="usercircle" />}
                  RightContent={<AltoIcon name="chevronright" />}
                  title={getDisplayFullName(user)}
                  descriptions={<Description>Personal & Insurance Info</Description>}
                  onPress={() => {
                    dispatch(push(`/me/${user.id}`));
                  }}
                />
              );
            })}
            {isFamilyMember ? null : (
              <ListItem
                LeftContent={<AltoIcon name="userplus" />}
                RightContent={<AltoIcon name="chevronright" />}
                title="Add a family member"
                onPress={() => {
                  dispatch(push('/me/family_member'));
                }}
              />
            )}
          </Card>
        </Main>
        <Sidebar>
          <SidebarItem
            buttonText="Invite A Friend"
            img={referral}
            imgAlt="Illustration of two friends"
            onClick={() => {
              setActiveActionSheet(<ReferralActionSheet />);
            }}
            text="Invite your friends and family to use Alto! Click below to generate a shareable link."
            inline
          />
        </Sidebar>
      </ContentWrapper>
    </PageBackground>
  );
};
